import SchemaParameterStore, { ISchemaParameter } from "./SchemaParameter/schema-parameter-store";
import {
  ArrayItemsSchema,
  type CompositionBranch,
  type CompositionKeyword,
  isSchemaParameterType,
} from "./types";

interface ValidationError {
  field: string;
  message: string;
  path: string[];
}

export const validateJson = (
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  json: any,
  path: string[] = [],
): ValidationError[] => {
  const errors: ValidationError[] = [];

  if (!json) {
    errors.push({
      field: path.join(".") || "root",
      message: `JSON content is empty or undefined at path: ${path.join(".")}`,
      path,
    });
    return errors;
  }

  // Validate required fields
  const requiredFields = ["name", "type", "title"];
  requiredFields.forEach(field => {
    if (!json[field]) {
      errors.push({
        field: path.length ? `${path.join(".")}.${field}` : field,
        message: `Missing required field: ${field} at path: ${path.join(".")}`,
        path: [...path, field],
      });
    }
  });

  // Validate type field
  if (json.type) {
    const validTypes = ["object", "array", "string", "number", "boolean", "variable", "null"];
    let isValid = false;

    if (Array.isArray(json.type)) {
      // Handle array of types like ["string", "null"]
      isValid = json.type.every((t: unknown) => typeof t === "string" && validTypes.includes(t));
    } else {
      isValid = typeof json.type === "string" && validTypes.includes(json.type);
    }

    if (!isValid) {
      errors.push({
        field: path.length ? `${path.join(".")}.type` : "type",
        message: `Invalid type: ${json.type} at path: ${path.join(
          ".",
        )}. Must be one of: object, array, string, number, boolean, variable or an array like ["string", "null"]`,
        path: [...path, "type"],
      });
    }
  }

  // Validate object properties
  if (json.type === "object" && json.properties) {
    Object.entries(json.properties).forEach(([key, value]) => {
      errors.push(...validateJson(value, [...path, "properties", key]));
    });
  }

  // Validate array items
  if (json.type === "array" && json.items) {
    if (!json.items.properties) {
      errors.push({
        field: path.length ? `${path.join(".")}.items` : "items",
        message: `Array items must have properties defined at path: ${path.join(".")}`,
        path: [...path, "items"],
      });
    } else {
      errors.push(...validateJson(json.items.properties, [...path, "items", "properties"]));
    }
  }

  return errors;
};

export const convertJsonToParameter = (
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  json: any,
  parent?: SchemaParameterStore,
  expandedStateMap?: Map<string, boolean>,
): SchemaParameterStore => {
  const param = new SchemaParameterStore(0, true, parent);
  param.name = json.name;
  param.title = json.title;
  param._description = json.description || "";

  // Handle type - could be a string or an array like ["string", "null"]
  if (Array.isArray(json.type)) {
    // Backward compatibility: handle array format like ["string", "null"]
    const types = json.type.filter(
      (t: unknown): t is string => typeof t === "string" && t !== "null",
    );
    if (types.length > 0) {
      const firstType = types[0];
      // Use the first non-null type, defaulting to "string" if not a valid SchemaParameterType
      param.type = isSchemaParameterType(firstType) ? firstType : "string";
      param.nullable = json.type.some((t: unknown) => t === "null");
    } else {
      // If all types are "null", default to string and mark as nullable
      param.type = "string";
      param.nullable = true;
    }
  } else if (typeof json.type === "string") {
    // Validate single type value
    param.type = isSchemaParameterType(json.type) ? json.type : "string";
    // Handle nullable: true property format (works with both OpenAI and Gemini)
    if (json.nullable === true) {
      param.nullable = true;
    }
  }

  if (json.minLength !== undefined) {
    param.minLength = json.minLength;
  }
  if (json.maxLength !== undefined) {
    param.maxLength = json.maxLength;
  }
  if (json.pattern !== undefined) {
    param.pattern = json.pattern;
  }
  if (json.format !== undefined) {
    param.format = json.format;
  }
  if (json.minimum !== undefined) {
    param.minimum = json.minimum;
  }
  if (json.maximum !== undefined) {
    param.maximum = json.maximum;
  }
  if (json.multipleOf !== undefined) {
    param.multipleOf = json.multipleOf;
  }
  if (json.exclusiveMinimum !== undefined) {
    param.exclusiveMinimum = json.exclusiveMinimum;
  }
  if (json.exclusiveMaximum !== undefined) {
    param.exclusiveMaximum = json.exclusiveMaximum;
  }
  if (json.minItems !== undefined) {
    param.minItems = json.minItems;
  }
  if (json.maxItems !== undefined) {
    param.maxItems = json.maxItems;
  }
  if (json.$ref !== undefined) {
    param.$ref = json.$ref;
  }

  if (
    json.const !== undefined &&
    (typeof json.const === "string" ||
      typeof json.const === "number" ||
      typeof json.const === "boolean")
  ) {
    param.constValue = json.const;
    if (json.type === undefined) {
      if (typeof json.const === "string") {
        param.type = "string";
      } else if (typeof json.const === "number") {
        param.type = "number";
      } else {
        param.type = "boolean";
      }
    }
  }

  const composition = parseCompositionFromProperty(json);
  if (composition) {
    param.compositionKeyword = composition.keyword;
    param.compositionBranches = composition.branches;
    const rawRequirements = json.required || json?.items?.required || [];
    param._required = (rawRequirements as unknown as string[])
      .map(name => param.children.find(p => p.name === name))
      .filter((p): p is SchemaParameterStore => p !== undefined);
    return param;
  }

  const rawRequirements = json.required || json?.items?.required || [];

  // Handle variable type
  if (json.type === "variable") {
    param.variableName = json.name;
  }

  // Handle enum variables properly
  if (json.enum) {
    if (typeof json.enum === "object" && json.enum.type === "enum_variable") {
      // This is an enum variable
      param.setEnums(json.enum);
    } else if (Array.isArray(json.enum)) {
      // This is a regular enum array
      param.setEnums(json.enum);
    }
  }

  // Restore expanded state if provided
  if (expandedStateMap && json.name && expandedStateMap.has(json.name)) {
    if (expandedStateMap.get(json.name)) {
      param._collapsed = false;
    }
  }

  // Helper to check if type matches (handles both string and array types like ["array", "null"])
  const isType = (typeValue: unknown, targetType: string): boolean => {
    if (Array.isArray(typeValue)) {
      return typeValue.includes(targetType);
    }
    return typeValue === targetType;
  };

  // Handle object with properties
  if (isType(json.type, "object") && json.properties) {
    // Create nested expanded state map if possible
    const nestedExpandedStateMap = new Map<string, boolean>();
    param.children.forEach(child => {
      nestedExpandedStateMap.set(child.name, child.isExpanded);
    });

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    param.children = Object.entries(json.properties || {}).map(([key, value]: [string, any]) => {
      const childParam = convertJsonToParameter(
        { ...value, name: key },
        param,
        nestedExpandedStateMap,
      );
      return childParam;
    });
  }
  // Handle array
  else if (isType(json.type, "array") && json.items) {
    const nestedExpandedStateMap = new Map<string, boolean>();
    param.children.forEach(child => {
      nestedExpandedStateMap.set(child.name, child.isExpanded);
    });

    if (json.items.properties) {
      // Composition array (array of objects)
      param.children = Object.entries(json.items.properties || {}).map(([key, value]) => {
        const childParam = convertJsonToParameter(
          { ...(value as object), name: key },
          param,
          nestedExpandedStateMap,
        );
        return childParam;
      });
      param.items = undefined;
    } else {
      // Simple array (array of primitives)
      param.items = json.items as ArrayItemsSchema;
      param.children = [];
    }
  }

  // Resolve required fields
  param._required = (rawRequirements as unknown as string[])
    .map(name => param.children.find(p => p.name === name))
    .filter((p): p is SchemaParameterStore => p !== undefined);

  return param;
};

export const convertParameterToJson = (
  param: ISchemaParameter,
  allowAdditionalProperties = false,
): {
  name: string;
  type?: string | [string, "null"];
  description?: string;
  title?: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  properties?: Record<string, any>;
  required?: string[];
  additionalProperties?: boolean;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  items?: any;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  enum?: any;
  // String constraints
  minLength?: string;
  maxLength?: string;
  pattern?: string;
  format?: string;
  // Number constraints
  minimum?: string;
  maximum?: string;
  multipleOf?: string;
  exclusiveMinimum?: string;
  exclusiveMaximum?: string;
  // Array constraints
  minItems?: string;
  maxItems?: string;
  // Reference
  $ref?: string;
} => {
  // Handle variable type
  if (param.type === "variable") {
    return {
      type: "variable",
      name: param.variableName || "",
      description: "",
      title: param.title,
    };
  }

  const getEnumValue = (): { name: string; type: string } | string[] | undefined => {
    if (!param.hasEnums) {
      return undefined;
    }

    if (param.isEnumVariable) {
      return { name: param.enumName || "", type: "enum_variable" };
    }

    return param.enum;
  };

  // Only include required if it has values
  const requiredNames = param._required?.map(p => p?.name || "invalid_reference1");
  const required = requiredNames && requiredNames.length > 0 ? requiredNames : undefined;

  // $ref cannot have sibling keywords per JSON Schema spec.
  // Azure OpenAI strictly enforces this and rejects schemas that include
  // type/description/etc. alongside $ref.
  if (param.$ref) {
    return { name: param.name, $ref: param.$ref };
  }

  if (param.compositionKeyword && param.compositionBranches.length > 0) {
    return { name: param.name, ...buildCompositionPropertyJson(param) };
  }

  if (param.constValue !== undefined) {
    const constSchema: Record<string, unknown> = { const: param.constValue };
    if (param.description) {
      constSchema.description = param.description;
    }
    if (param.title) {
      constSchema.title = param.title;
    }
    return { name: param.name, ...constSchema };
  }

  const baseObj = {
    name: param.name,
    type: param.nullable ? ([param.type, "null"] as [string, "null"]) : param.type,
    description: param.description,
    title: param.title || undefined,
    required,
    enum: getEnumValue(),
    maxLength: param.maxLength,
    minLength: param.minLength,
    pattern: param.pattern,
    format: param.format,
    minimum: param.minimum,
    maximum: param.maximum,
    multipleOf: param.multipleOf,
    exclusiveMinimum: param.exclusiveMinimum,
    exclusiveMaximum: param.exclusiveMaximum,
    minItems: param.minItems,
    maxItems: param.maxItems,
  };

  // Object with nested properties
  if (param.type === "object" && param.children.length > 0) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const properties: Record<string, any> = {};

    param.children.forEach(childParam => {
      properties[childParam.name] = {
        ...convertParameterToJson(childParam),
        name: undefined,
      };
    });

    return {
      ...baseObj,
      additionalProperties: allowAdditionalProperties,
      properties,
    };
  }

  // Composition array (array of objects with properties)
  if (param.type === "array" && param.children.length > 0) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const properties: Record<string, any> = {};

    param.children.forEach(childParam => {
      properties[childParam.name] = {
        ...convertParameterToJson(childParam),
        name: undefined,
      };
    });
    return {
      ...baseObj,
      required: undefined,
      items: {
        properties: properties,
        required: baseObj.required,
        type: "object",
        additionalProperties: allowAdditionalProperties,
      },
    };
  }

  // Simple array (array of primitives)
  if (param.type === "array" && param.items) {
    return {
      ...baseObj,
      required: undefined, // Simple arrays don't have required fields
      items: {
        ...param.items,
      },
    };
  }

  return baseObj;
};

/** Keys the interactive JSON Schema editor treats as “standard” at any nesting depth (beyond `properties` / `$defs` children). */
export const SCHEMA_EDITOR_PRESET_FIELD_KEYS: readonly string[] = [
  "items",
  "name",
  "description",
  "title",
  "type",
  "nullable",
  "required",
  "properties",
  "schema",
  "parameters",
  "input_schema",
  "additionalProperties",
  "strict",
  "enum",
  "minLength",
  "maxLength",
  "pattern",
  "format",
  "$ref",
  // Composition & metadata (advanced schemas still use the JSON panel; see structured-output-json-schema skill)
  "oneOf",
  "anyOf",
  "allOf",
  "const",
  "$schema",
  "$id",
  "default",
  "definitions",
  "minimum",
  "maximum",
  "exclusiveMinimum",
  "exclusiveMaximum",
  "multipleOf",
  "minItems",
  "maxItems",
];

const COMPOSITION_KEYS = ["oneOf", "anyOf", "allOf"] as const;

const PRESERVED_ROOT_SCHEMA_KEYS = ["$schema", "$id", "title", "$comment", "description"] as const;

/** Root-level JSON Schema metadata preserved when rebuilding from the interactive editor. */
export function mergePreservedRootSchemaFields(
  built: Record<string, unknown>,
  source: Record<string, unknown> | undefined,
): Record<string, unknown> {
  if (!source) {
    return built;
  }
  const merged = { ...built };
  for (const key of PRESERVED_ROOT_SCHEMA_KEYS) {
    if (
      Object.prototype.hasOwnProperty.call(source, key) &&
      !Object.prototype.hasOwnProperty.call(merged, key)
    ) {
      merged[key] = source[key];
    }
  }
  return merged;
}

export function parseCompositionFromProperty(json: unknown): {
  keyword: CompositionKeyword;
  branches: CompositionBranch[];
} | null {
  if (!json || typeof json !== "object" || Array.isArray(json)) {
    return null;
  }
  const record = json as Record<string, unknown>;
  for (const key of COMPOSITION_KEYS) {
    if (!Object.prototype.hasOwnProperty.call(record, key)) {
      continue;
    }
    const raw = record[key];
    const list = Array.isArray(raw) ? raw : [raw];
    const branches: CompositionBranch[] = list.map((branch: unknown) => {
      if (
        branch &&
        typeof branch === "object" &&
        !Array.isArray(branch) &&
        typeof (branch as { $ref?: unknown }).$ref === "string"
      ) {
        return { $ref: (branch as { $ref: string }).$ref };
      }
      return {
        inlineSchema:
          branch && typeof branch === "object" && !Array.isArray(branch)
            ? structuredClone(branch as Record<string, unknown>)
            : {},
      };
    });
    return { keyword: key, branches };
  }
  return null;
}

/** JSON Schema fragment for a composition property (no editor-internal ``name`` field). */
export function buildCompositionPropertyJson(param: ISchemaParameter): {
  description?: string;
  title?: string;
  oneOf?: unknown[];
  anyOf?: unknown[];
  allOf?: unknown[];
} {
  const keyword = param.compositionKeyword as CompositionKeyword;
  const branches = param.compositionBranches.map(branch => {
    if (branch.$ref) {
      return { $ref: branch.$ref };
    }
    return branch.inlineSchema ?? {};
  });
  const result: {
    description?: string;
    title?: string;
    oneOf?: unknown[];
    anyOf?: unknown[];
    allOf?: unknown[];
  } = { [keyword]: branches };
  if (param.description) {
    result.description = param.description;
  }
  if (param.title) {
    result.title = param.title;
  }
  return result;
}

/** Resolve ``#/$defs/Name`` or ``#/definitions/Name`` to a display label. */
export function compositionRefDisplayName(ref: string): string {
  const defsMatch = ref.match(/^#\/\$defs\/(.+)$/);
  if (defsMatch) {
    return defsMatch[1];
  }
  const legacyMatch = ref.match(/^#\/definitions\/(.+)$/);
  if (legacyMatch) {
    return legacyMatch[1];
  }
  return ref;
}

/** True if any **property** value uses JSON Schema composition (union types). */
export function schemaPropertyUsesComposition(jsonSchema: unknown): boolean {
  if (!jsonSchema || typeof jsonSchema !== "object" || Array.isArray(jsonSchema)) {
    return false;
  }
  const props = (jsonSchema as Record<string, unknown>).properties;
  if (!props || typeof props !== "object" || Array.isArray(props)) {
    return false;
  }
  for (const val of Object.values(props)) {
    if (!val || typeof val !== "object" || Array.isArray(val)) {
      continue;
    }
    if (COMPOSITION_KEYS.some(k => Object.prototype.hasOwnProperty.call(val, k))) {
      return true;
    }
  }
  return false;
}

/** Deep scan for JSON Schema `oneOf` (structured outputs / tools). */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function jsonSchemaContainsOneOf(node: any): boolean {
  if (node === null || node === undefined) {
    return false;
  }
  if (Array.isArray(node)) {
    return node.some(item => jsonSchemaContainsOneOf(item));
  }
  if (typeof node !== "object") {
    return false;
  }
  if (Object.prototype.hasOwnProperty.call(node, "oneOf")) {
    return true;
  }
  return Object.values(node).some(v => jsonSchemaContainsOneOf(v));
}

/** Claude structured outputs document `anyOf`/`allOf` but not `oneOf`; Bedrock Claude paths are similar. */
export function isAnthropicFamilyStructuredOutputProvider(
  providerName: string | null | undefined,
): boolean {
  if (!providerName) {
    return false;
  }
  const n = providerName.toLowerCase();
  return n === "anthropic" || n.startsWith("anthropic.") || n === "amazon.bedrock";
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const validatePresetFields = (json: any, presets: string[]): boolean => {
  if (typeof json !== "object" || json === null) {
    return true;
  }

  return Object.keys(json).every(key => {
    // Skip preset validation for direct children of properties / defs
    if (key === "properties" || key === "$defs" || key === "definitions") {
      return Object.keys(json[key]).every(propKey =>
        validatePresetFields(json[key][propKey], presets),
      );
    }

    if (!presets.includes(key)) {
      return false;
    }

    if (typeof json[key] === "object" && json[key] !== null) {
      if (Array.isArray(json[key])) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        return json[key].every((item: any) => validatePresetFields(item, presets));
      }
      return validatePresetFields(json[key], presets);
    }

    return true;
  });
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const stripDummyFields = (json: any): any => {
  const FIELDS_TO_REMOVE = ["isRequired", "isEnum", "hasAdditionalProperties"];

  if (!json || typeof json !== "object") {
    return json;
  }

  if (Array.isArray(json)) {
    return json.map(item => stripDummyFields(item));
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const result: any = {};

  for (const [key, value] of Object.entries(json)) {
    if (FIELDS_TO_REMOVE.includes(key)) {
      continue;
    }

    if (typeof value === "object" && value !== null) {
      result[key] = stripDummyFields(value);
    } else {
      result[key] = value;
    }
  }

  return result;
};

export const checkForJinja = (value: string) => {
  // Basic patterns
  const containsBasicJinja = /\{\{[\s\S]*?\}\}|\{%[\s\S]*?%\}|\{#[\s\S]*?#\}/.test(value);

  // Check if it's nested inside JSON structure
  const isNestedInJSON = (() => {
    try {
      // Try to parse as JSON
      JSON.parse(value);
      return true;
    } catch {
      // Check for partial JSON patterns with nested braces
      const jsonPatterns = [
        // Object with nested objects/arrays
        /\{[^{}]*\{[^{}]*\}[^{}]*\}/,
        // Array with objects
        /\[[^[\]]*\{[^{}]*\}[^[\]]*\]/,
        // Property value as object
        /:\s*\{(?!\w+\})[^{}]*\}/,
        // Check for typical JSON structure
        /"[^"]+"\s*:\s*\{?\s*"[^"]+"\s*:\s*"[^"]+"\s*\}?/,
      ];
      return jsonPatterns.some(pattern => pattern.test(value));
    }
  })();

  if (isNestedInJSON) {
    return true;
  }

  if (!containsBasicJinja) {
    return false;
  }

  // Additional checks to reduce false positives

  // Check if it's in a code block example
  const isCodeExample =
    /```[\s\S]*?\{\{[\s\S]*?\}\}[\s\S]*?```/.test(value) || /`\{\{[\s\S]*?\}\}`/.test(value);

  // Check if it's in quotes describing Jinja
  const isQuotedExample = /".*?\{\{.*?\}\}.*?"/.test(value) || /'.*?\{\{.*?\}\}.*?'/.test(value);

  // Check for valid Jinja context (more sophisticated)
  const validJinjaContexts = [
    // Variable output with filters
    /\{\{\s*[\w.]+(\s*\|\s*\w+(\([^)]*\))?)*\s*\}\}/,

    // Control statements
    /\{%\s*(if|for|set|block|extends|include|macro|call)\s+.*?%\}/,

    // End blocks
    /\{%\s*(endif|endfor|endblock|endmacro)\s*%\}/,
  ];

  const hasValidContext = validJinjaContexts.some(pattern => pattern.test(value));

  // If it looks like a code example, quoted example, or nested JSON but doesn't have valid Jinja context,
  // it's more likely a false positive
  if ((isCodeExample || isQuotedExample) && !hasValidContext) {
    return false;
  }

  return true;
};
